


































































import { Branch, getBranchName } from "@/data/models/Branch";
import Client from "@/data/models/Client";
import { 
  getClientId, 
  getClientName, 
  getMemberClientsStrictBranchFiltersConfiguration,
  hasClientEnabledStrictBranchFilters
} from "@/data/repository/ClientRepository";
import { moveHyDryDevicesToNewClient } from "@/data/repository/IresDeviceRepository";
import { firestore } from "firebase";
import { Component, Vue, Ref, Prop, Watch } from "vue-property-decorator";
import BaseDialogComponent from '../common/BaseDialog.vue';
import ConfirmationDialogComponent from '../common/ConfirmationDialog.vue';
import DatePickerCompoment from '../common/DatePicker.vue';
import PickerDialogComponent from '../common/PickerDialog.vue';
import { IresDeviceItemData } from "@/data/models/IresDeviceItemData";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "date-picker": DatePickerCompoment,
    picker: PickerDialogComponent,
  },
})

export default class MoveIresDevicesDialog extends Vue {
  @Prop({ default: () => new Map<string, Client>() })
  memberClients!: Map<string, Client>;

  @Prop()
  selectedIresDevices!: IresDeviceItemData[];

  @Prop({ default: false })
  isAllowedToMoveDevicesToAnotherClient!: boolean;

  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;
  
  title = "";

  showingSuccessSnackbar = false;
  successSnackbarText = "";

  showingErrorSnackbar = false;
  errorSnackbarText = "";

  isLoading = false;

  resolve?: (value: boolean) => void = undefined;
  reject?: (reason: string) => void = undefined;

  assignmentStartDate: firestore.Timestamp = firestore.Timestamp.now();
  memberClientId: string | null = null;
  branch: string | null | undefined = null;

  hasStrictBranchFiltersEnabled = hasClientEnabledStrictBranchFilters();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialDetails: any = {};

  cancelButtonText = this.$t("common.abort").toString();
  closeButtonText = this.$t("common.close").toString();
  saveButtonText = this.$t("common.save").toString();

  @Watch("hasDetailsEdited")
  onHasDetailsEditedChanged() {
    this.baseDialogComponent.negativeButtonText = this.calculateNegativeButtonText();
  }

  @Watch("memberClientId")
  async onMemberClientIdChanged() {
    if (!this.memberClientId) {
      return;
    }

    const client = this.memberClients.get(this.memberClientId);

    if (client) {
      this.hasStrictBranchFiltersEnabled = await getMemberClientsStrictBranchFiltersConfiguration(client);
    }
  }

  get memberClientFormatter() {
    return (memberClientId: string) => {
      return getClientName(this.memberClients.get(memberClientId) ?? null);
    };
  }

  get branchFormatter() {
    return (branchId: string) => {
      return getBranchName(this.clientBranches.find((branch) => branchId === branch.id));
    };
  }

  get memberClientOptions() {
    return Array.from(this.memberClients.values())
      .sort((a: Client, b: Client) => {
        if (a.hasAdministrationCapabilities && !b.hasAdministrationCapabilities) {
          return -1;
        } else if (!a.hasAdministrationCapabilities && b.hasAdministrationCapabilities) {
          return 1;
        }

        const nameA = a.companyName || a.clientName;
        const nameB = b.companyName || b.clientName;
        
        return nameA.localeCompare(nameB);
      })
      .map((client: Client) => client.id);
  }

  get clientBranches() {
    if (!this.memberClientId) {
      return [];
    }

    const client = this.memberClients.get(this.memberClientId);

    if (!client || !client.branches || client.branches.length === 0) {
      return [];
    }

    const branches: Branch[] = [];
    const keys = Object.keys(client.branches);
    
    for (const key of keys) {
      branches.push(client.branches[key] as Branch);
    }

    return branches;
  }

  get branchOptions() {
    return this.clientBranches.map((branch: Branch) => branch.id);
  }

  get hasDetailsEdited() {
    const branchSelectionOptions = this.showSelectNothingOptionForBranchSelector ||
      (this.branch !== null && this.branch !== undefined);

    return this.isAllowedToMoveDevicesToAnotherClient
      ? this.memberClientId !== null && branchSelectionOptions
      : branchSelectionOptions;
  }

  get minAssignmentStartDate() {
    return firestore.Timestamp.now();
  }

  get showBranchSelector() {
    return this.branchOptions.length > 0;
  }

  get showSelectNothingOptionForBranchSelector() {
    return !this.hasStrictBranchFiltersEnabled;
  }

  getBranchNumber(branchId: string | null | undefined) {
    if (!branchId) {
      return null;
    }

    const branches = this.clientBranches;
    const deviceBranch = branches.filter(branch => branchId === branch.id.toString());

    if (deviceBranch.length === 1) {
      return deviceBranch[0].branchNumber.toString();
    }

    return null;
  }

  restoreIntialValues() {
    this.isLoading = false;

    this.assignmentStartDate = firestore.Timestamp.now();
    this.memberClientId = this.isAllowedToMoveDevicesToAnotherClient
      ? null
      : getClientId();
    this.branch = null;

    this.initialDetails = {
      assignmentStartDate: this.assignmentStartDate,
      memberClientId: this.memberClientId,
      branch: this.branch
    }
  }

  onAbortMoveHyDryDevicesConfirmed() {
    this.restoreIntialValues();
    this.baseDialogComponent.isShowing = false;
  }

  async onSave() {
    this.isLoading = true;

    if (this.memberClientId) {
      const branchNumber = this.branch 
        ? this.getBranchNumber(this.branch) 
        : this.showSelectNothingOptionForBranchSelector
          ? null 
          : undefined;

      const numberOfDevicesMoved = await moveHyDryDevicesToNewClient(
        this.selectedIresDevices.map((iresDeviceItem) => iresDeviceItem.serialNumber),
        this.memberClientId,
        this.assignmentStartDate,
        branchNumber,
      );
    
      if (numberOfDevicesMoved !== -1) {
        this.showingSuccessSnackbar = true;
        this.successSnackbarText = this.$tc("iresDevice.moveDevicesDialog.successMessage", numberOfDevicesMoved).toString();
        this.baseDialogComponent.isShowing = false;
        
        this.$emit("clearSelectedIresDevices");
      }
      else {
        this.showingErrorSnackbar = true;
        this.errorSnackbarText = this.$t("iresDevice.moveDevicesDialog.errorMessage").toString();
      }
    }

    this.isLoading = false;
  }

  async onCancel() {
    if (this.hasDetailsEdited) {
      const result = await this.confirmationDialogComponent.show({
        title: this.$t("common.confirmationDialog.cancelTitle").toString(),
        message: this.$t("common.confirmationDialog.cancelMessage").toString(),
      });

      if (result) {
        this.onAbortMoveHyDryDevicesConfirmed();
      }
    }
    else {
      this.onAbortMoveHyDryDevicesConfirmed();
    }
  }

  async show() {
    this.title = this.$t("iresDevice.moveDevicesDialog.title").toString();

    const positiveButtonText = this.saveButtonText;
    const negativeButtonText = this.calculateNegativeButtonText();

    this.baseDialogComponent.show({
      title: this.title,
      positiveButtonText: positiveButtonText,
      negativeButtonText: negativeButtonText,
      closeOnSave: false,
      maxWidth: this.isAllowedToMoveDevicesToAnotherClient ? 600 : 400,
    });

    this.restoreIntialValues();

    return new Promise<boolean>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  calculateNegativeButtonText() {
    return !this.hasDetailsEdited
      ? this.closeButtonText
      : this.cancelButtonText;
  }
}
